import {useCallback, useEffect, useMemo, useRef, useState} from "react"
import axios from "axios"
import {getAuthConfig} from "../../utils"
import {GetContactsAndRoles} from "../../api/APIContact"
import {getAnonymousName} from "../../DataFunctions"
import {getCurrentQuarterRange} from "./academicCalendar"

const auth = getAuthConfig()
const PAGE_SIZE = 50

async function fetchSessionPage({updated, id, limit = PAGE_SIZE, survey, contact} = {}) {
    const params = new URLSearchParams()
    if (updated) params.append("updated", updated)
    if (id) params.append("id", id)
    params.append("limit", limit.toString())
    if (survey) params.append("survey", survey)
    if (contact) params.append("contact", contact)
    const queryString = params.toString()
    const {data} = await axios.get(
        `/api/v2/session.php/${queryString ? "?" + queryString : ""}`,
        auth
    )
    return data
}

/**
 * Groups sessions by contact, using `created` (fallback `updated`) as the
 * recency signal so re-analysis doesn't reorder contacts.
 */
function groupSessionsByContact(sessions) {
    const contactMap = new Map()

    for (const session of sessions) {
        const contactId = session.contact
        const sessionDate = session.created || session.updated

        if (!contactMap.has(contactId)) {
            contactMap.set(contactId, {
                contactId,
                sessions: [],
                mostRecentCreated: sessionDate,
            })
        }
        const group = contactMap.get(contactId)
        group.sessions.push(session)
        if (sessionDate > group.mostRecentCreated) {
            group.mostRecentCreated = sessionDate
        }
    }

    const contacts = Array.from(contactMap.values())
    contacts.sort((a, b) => b.mostRecentCreated.localeCompare(a.mostRecentCreated))
    return contacts
}

export default function useSessionData({preFilterSurvey, preFilterContact} = {}) {
    const [sessions, setSessions] = useState([])
    const [loadingPhase, setLoadingPhase] = useState("idle") // idle, initial, background, done, extending
    const [hasMore, setHasMore] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)

    const cursorRef = useRef({updated: null, id: null})
    const abortRef = useRef(false)

    // Refs for mutable state accessed inside async loops without stale closures
    const hasMoreRef = useRef(true)
    const loadingPhaseRef = useRef("idle")

    // Used to cancel in-flight ensureLoadedThrough when refresh is called
    const extendVersionRef = useRef(0)

    const quarterBoundary = useMemo(() => getCurrentQuarterRange().start, [])

    const contactsQuery = GetContactsAndRoles()
    const contactsMap = useMemo(() => {
        const map = new Map()
        const contactsData = contactsQuery.data?.contacts || contactsQuery.data || []
        if (Array.isArray(contactsData)) {
            contactsData.forEach(c => map.set(c.id, c))
        }
        return map
    }, [contactsQuery.data])

    const loadPage = useCallback(async () => {
        try {
            const result = await fetchSessionPage({
                updated: cursorRef.current.updated,
                id: cursorRef.current.id,
                limit: PAGE_SIZE,
                survey: preFilterSurvey,
                contact: preFilterContact,
            })

            const newSessions = Array.isArray(result) ? result : (result?.sessions || result?.data || [])
            const responseHasMore = result?.has_more ?? newSessions.length === PAGE_SIZE

            if (newSessions.length > 0) {
                const lastSession = newSessions[newSessions.length - 1]
                cursorRef.current = {
                    updated: lastSession.updated,
                    id: lastSession.id,
                }
            }

            setHasMore(responseHasMore)
            hasMoreRef.current = responseHasMore
            setSessions(prev => [...prev, ...newSessions])

            return {newSessions, hasMore: responseHasMore}
        } catch (err) {
            console.error("Failed to fetch sessions:", err)
            setHasMore(false)
            hasMoreRef.current = false
            return {newSessions: [], hasMore: false}
        }
    }, [preFilterSurvey, preFilterContact])

    // Tier 1 + 2: Initial load + background load to current quarter boundary
    useEffect(() => {
        let cancelled = false
        abortRef.current = false

        const updatePhase = (phase) => {
            loadingPhaseRef.current = phase
            setLoadingPhase(phase)
        }

        async function load() {
            updatePhase("initial")
            const firstResult = await loadPage()

            if (cancelled || abortRef.current) return
            if (!firstResult.hasMore) {
                updatePhase("done")
                return
            }

            updatePhase("background")

            let keepGoing = firstResult.hasMore
            while (keepGoing && !cancelled && !abortRef.current) {
                const result = await loadPage()
                if (cancelled || abortRef.current) return

                if (!result.hasMore || result.newSessions.length === 0) {
                    keepGoing = false
                    break
                }

                const lastSession = result.newSessions[result.newSessions.length - 1]
                const lastDate = new Date(lastSession.updated)
                if (lastDate <= quarterBoundary) {
                    keepGoing = false
                }
            }

            if (!cancelled) {
                updatePhase("done")
            }
        }

        load()
        return () => {
            cancelled = true
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preFilterSurvey, preFilterContact])

    /**
     * Ensures sessions are loaded back at least to `boundaryDate` (compared
     * against the `updated` cursor — safe because updated >= created).
     *
     * Short-circuits if already covered, no more pages, or a load is in progress.
     * Re-run this from an effect that also depends on loadingPhase so it fires
     * once the mount load finishes.
     */
    const ensureLoadedThrough = useCallback(async (boundaryDate) => {
        // Only act when the initial/background load has settled
        if (loadingPhaseRef.current !== "done") return
        // Nothing loaded yet (mount hasn't finished)
        if (!cursorRef.current.updated) return
        // All pages already loaded
        if (!hasMoreRef.current) return
        // Already covered: the oldest loaded `updated` is before/at the boundary
        if (new Date(cursorRef.current.updated) <= boundaryDate) return

        const version = ++extendVersionRef.current

        loadingPhaseRef.current = "extending"
        setLoadingPhase("extending")

        while (true) {
            // Cancelled by a newer call (e.g. user selected a different range or hit refresh)
            if (extendVersionRef.current !== version) return

            const result = await loadPage()

            if (!result.hasMore || result.newSessions.length === 0) break

            const lastSession = result.newSessions[result.newSessions.length - 1]
            if (new Date(lastSession.updated) <= boundaryDate) break
        }

        if (extendVersionRef.current === version) {
            loadingPhaseRef.current = "done"
            setLoadingPhase("done")
        }
    }, [loadPage])

    const refreshAll = useCallback(async (boundaryDate) => {
        extendVersionRef.current++ // cancel any ongoing ensureLoadedThrough
        setIsRefreshing(true)
        setSessions([])
        setHasMore(true)
        hasMoreRef.current = true
        cursorRef.current = {updated: null, id: null}
        abortRef.current = true

        const updatePhase = (phase) => {
            loadingPhaseRef.current = phase
            setLoadingPhase(phase)
        }

        updatePhase("initial")

        try {
            const firstResult = await fetchSessionPage({
                limit: PAGE_SIZE,
                survey: preFilterSurvey,
                contact: preFilterContact,
            })
            const newSessions = Array.isArray(firstResult)
                ? firstResult
                : (firstResult?.sessions || firstResult?.data || [])
            const responseHasMore = firstResult?.has_more ?? newSessions.length === PAGE_SIZE

            if (newSessions.length > 0) {
                cursorRef.current = {
                    updated: newSessions[newSessions.length - 1].updated,
                    id: newSessions[newSessions.length - 1].id,
                }
            }

            setSessions(newSessions)
            setHasMore(responseHasMore)
            hasMoreRef.current = responseHasMore

            if (!responseHasMore) {
                updatePhase("done")
                setIsRefreshing(false)
                return
            }

            updatePhase("background")

            let keepGoing = responseHasMore
            while (keepGoing) {
                const result = await loadPage()
                if (!result.hasMore || result.newSessions.length === 0) {
                    keepGoing = false
                    break
                }
                const lastSession = result.newSessions[result.newSessions.length - 1]
                if (new Date(lastSession.updated) <= boundaryDate) {
                    keepGoing = false
                }
            }
        } catch (err) {
            console.error("Failed to refresh sessions:", err)
        }

        updatePhase("done")
        setIsRefreshing(false)
    }, [preFilterSurvey, preFilterContact, loadPage])

    /**
     * Loads a single session that may live outside the lazily-loaded window (e.g. a
     * shared deep-link to an old conversation). Fetches the session to learn its
     * contact, then pulls that contact's full thread so the detail panel timeline is
     * complete. Merges into `sessions` deduped by id (existing entries win).
     */
    const ensureSessionLoaded = useCallback(async (sessionId) => {
        try {
            const {data} = await axios.get(`/api/v2/session.php/${sessionId}`, auth)
            const session = Array.isArray(data) ? data[0] : data
            if (!session) return null

            let fetched = [session]
            if (session.contact) {
                const contactData = await fetchSessionPage({contact: session.contact, limit: 100})
                const contactSessions = Array.isArray(contactData)
                    ? contactData
                    : (contactData?.sessions || contactData?.data || [])
                if (contactSessions.length > 0) fetched = contactSessions
            }

            setSessions(prev => {
                const existing = new Set(prev.map(s => s.id))
                const additions = fetched.filter(s => !existing.has(s.id))
                return additions.length > 0 ? [...prev, ...additions] : prev
            })
            return session
        } catch (err) {
            console.error("Failed to load session:", err)
            return null
        }
    }, [])

    const refreshSession = useCallback(async (sessionId) => {
        try {
            const {data} = await axios.get(`/api/v2/session.php/${sessionId}`, auth)
            const updatedSession = Array.isArray(data) ? data[0] : data
            if (updatedSession) {
                setSessions(prev => prev.map(s =>
                    s.id === sessionId ? updatedSession : s
                ))
            }
            return updatedSession || null
        } catch (err) {
            console.error("Failed to refresh session:", err)
            return null
        }
    }, [])

    const contactGroups = useMemo(() => groupSessionsByContact(sessions), [sessions])

    const contactsWithNames = useMemo(() => {
        return contactGroups.map(group => ({
            ...group,
            contactName: getAnonymousName(group.contactId),
        }))
    }, [contactGroups])

    return {
        contacts: contactsWithNames,
        sessions,
        loadingPhase,
        totalSessions: sessions.length,
        hasMore,
        ensureLoadedThrough,
        ensureSessionLoaded,
        refreshSession,
        refreshAll,
        isRefreshing,
        contactsMap,
    }
}
